import React, { useState, useRef, useEffect } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import paths from 'app/paths'
import { useOrganization } from 'contexts/organization'
import { useSmartTemplates } from 'contexts/smart-templates'
import { usePossum } from 'hooks/possum'
import { cls } from 'utilities/dom'
import { ModalHeader } from 'modals/generic'
import Form from 'components/form/controller'
import EntityField from 'components/form/field/entity'
import { SmartTemplate } from './s'
import Paragraph from 'components/typography/paragraph'
import Message from 'components/message'
import Actions from 'components/form/actions'
import { Plain, Button } from 'components/button'

const AddToTemplates = ({ mode = 'predefined', template: initialTemplate = null, modal, salt }) => {
    const { formatMessage } = useIntl()

    const navigate = useNavigate()

    const { organization } = useOrganization()
    const { name } = organization ?? {}

    const {
        libraryTemplates,
        hasFetchedLibraryTemplates,

        fetchLibraryTemplates,
        fetchTemplate
    } = useSmartTemplates()

    const possessify = usePossum()

    const [template, setTemplate] = useState(initialTemplate)
    const [templatePickerTriggered, setTemplatePickerTriggered] = useState(false)
    const [fetching, setFetching] = useState(false)

    const templatePickerRef = useRef()

    useEffect(() => {
        if(mode === 'pick' && !hasFetchedLibraryTemplates) {
            fetchLibraryTemplates()
        }
    }, [mode, hasFetchedLibraryTemplates])

    useEffect(() => {
        if(mode === 'pick' && !!templatePickerRef?.current && !templatePickerTriggered && !template) {
            setTemplatePickerTriggered(true)
            templatePickerRef.current.click()
        }
    }, [mode, templatePickerTriggered, template])

    const addToTemplates = async () => {
        setFetching(true)
        const { ok, response } = await fetchTemplate(template.id, template.providedBy)
        setFetching(false)

        if(ok && response) {
            navigate(paths.documents.templates, {
                state: {
                    intent: 'duplicate',
                    libraryTemplate: response,
                    explicit: true
                }
            })

            modal.dismiss()
        }
    }

    return (
        <>
            <ModalHeader
                heading={formatMessage({
                    id: 'template_action_add_to_templates',
                    defaultMessage: 'Add to your templates'
                })}
                dismiss={modal.dismiss} />
            {(mode === 'pick') && (
                <Form layout="vertical">
                    <EntityField
                        salt={salt}
                        label={formatMessage({
                            id: 'documents_smart_template_noun',
                            defaultMessage: 'Smart template'
                        })}
                        name="template"
                        field={{
                            value: template ?? null,
                            include: 'never',
                            unsettable: true,
                            placeholder: formatMessage({
                                id: 'documents_smart_templates_action_pick',
                                defaultMessage: 'Pick smart template'
                            })
                        }}
                        picker={{
                            heading: formatMessage({
                                id: 'documents_smart_templates_action_pick',
                                defaultMessage: 'Pick smart template'
                            }),
                            search: {
                                placeholder: formatMessage({
                                    id: 'documents_smart_templates_action_find',
                                    defaultMessage: 'Find smart template'
                                })
                            },
                            entities: libraryTemplates,
                            outer: false
                        }}
                        entity={{
                            type: 'smartTemplate',
                            showIcon: false
                        }}
                        onChange={({ template }) => setTemplate(template)}
                        enabled={!fetching}
                        whistle={template?.id ?? 'none'}
                        ref={templatePickerRef} />
                </Form>
            )}
            {(mode === 'predefined') && (
                <SmartTemplate
                    template={template}
                    className="wrapped" />
            )}
            <Paragraph>
                <FormattedMessage
                    id="documents_smart_templates_add_to_templates_description"
                    defaultMessage="The selected template will be added to {pname} templates. You can then use it as a starting point for your own templates."
                    values={{ pname: possessify(name) }} />
            </Paragraph>
            {!!template && (
                <Message
                    type="info"
                    className="compact"
                    message={formatMessage({
                        id: 'documents_smart_templates_add_to_templates_message',
                        defaultMessage: '{company} will no longer be responsible for the content of this template, nor will updates from them be applied to it.'
                    }, { company: template.providedBy.name })} />
            )}
            <Actions>
                <Plain
                    className="neutral"
                    disabled={fetching}
                    onClick={modal.dismiss}>
                    <FormattedMessage
                        id="action_cancel"
                        defaultMessage="Cancel" />
                </Plain>
                <Button
                    className={cls([
                        'constructive',
                        fetching && 'loading'
                    ])}
                    disabled={fetching || !template}
                    onClick={addToTemplates}>
                    <FormattedMessage
                        id="action_add"
                        defaultMessage="Add" />
                </Button>
            </Actions>
        </>
    )
}

export default AddToTemplates